import { Observable, merge, type Subscription } from 'rxjs'
import {
  observable,
  computed,
  reaction,
  action,
  onBecomeObserved,
  onBecomeUnobserved,
  createAtom,
} from 'mobx'
import { distinctUntilChanged, filter, tap } from 'rxjs/operators'
import { type Task } from 'mobx-task'
import { unpair } from './rx-util'

/**
 * Creates an RxJS Observable stream from the specified `expression` which
 * refers to MobX observables.
 *
 * @param expression
 * @param fireImmediately
 */
export function toStream<T>(
  expression: () => T,
  fireImmediately = false,
): Observable<T> {
  return new Observable<T>((observer) => {
    const computedValue = computed(expression)
    return reaction(
      () => computedValue.get(),
      (newValue) => observer.next(newValue),
      { fireImmediately },
    )
  })
}

/**
 * Returns a getter function for the value of an observable that will
 * be updated any time the Rx stream emits.
 *
 * NOTE: The `stream` should complete when no longer used. Alternatively,
 * call the `dispose` method on the returned function, OR use `asAtom`.
 * This is because **the stream is KEPT ALIVE** otherwise.
 *
 * @param initialValue The initial value to emit on subscribe
 * @param stream The stream to observe.
 */
export function fromStream<T>(initialValue: T, stream: Observable<T>) {
  const box = observable.box(initialValue)
  const sub = stream.subscribe(action((value: T) => box.set(value)))

  function get() {
    return box.get()
  }

  get.dispose = function () {
    sub.unsubscribe()
  }

  return get
}

/**
 * An alternative to `fromStream`, will manage the subscription
 * based on MobX observability status.
 *
 * When the returned atom becomes observed, subscribes to the stream.
 * When it becomes unobserved, unsubscribes.
 */
export function asAtom<T>(stream: Observable<T>): () => T | null
export function asAtom<T>(stream: Observable<T>, defaultValue: T): () => T
export function asAtom<T>(stream: Observable<T>, defaultValue: () => T): () => T
export function asAtom<T>(
  stream: Observable<T>,
  initialValue: T | (() => T) | null = null,
): () => T | null {
  let subscription: Subscription | null = null
  let value =
    typeof initialValue === 'function'
      ? (initialValue as () => T)()
      : initialValue
  const atom = createAtom(
    'RxStream',
    () => {
      subscription = stream.subscribe({
        next: (newValue) => {
          value = newValue
          atom.reportChanged()
        },
      })
    },
    () => {
      subscription?.unsubscribe()
      subscription = null
    },
  )
  return () => {
    atom.reportObserved()
    return value
  }
}

/**
 * Creates an RxJS Observable stream that emits `true` when the observable becomes observed and
 * `false` when it becomes unobserved.
 */
export function beingObservedStream<
  T extends Record<K, unknown>,
  K extends keyof T,
>(target: T, prop: K): Observable<boolean> {
  const becameObserved$ = new Observable<boolean>((subscriber) => {
    const dispose = onBecomeObserved(target, prop, () => {
      subscriber.next(true)
    })
    return () => dispose()
  })

  const becameUnobserved$ = new Observable<boolean>((subscriber) => {
    const dispose = onBecomeUnobserved(target, prop, () => {
      subscriber.next(false)
    })
    return () => dispose()
  })

  return merge(becameObserved$, becameUnobserved$).pipe(distinctUntilChanged())
}

/**
 * Returns an RxJS Observable that emits the result of a task
 * after it completes. Does not emit immediately.
 *
 * @param task
 */
export function toTaskResultStream<A extends unknown[], R>(task: Task<A, R>) {
  return toStream((): [R, boolean] => [task.result as R, task.resolved]).pipe(
    filter(([, resolved]) => resolved),
    distinctUntilChanged(),
    unpair(),
  ) as Observable<Awaited<R>>
}

/**
 * Short-hand for `tap({ next: action(fn) })`.
 *
 * @param next
 */
export function tapAction<T>(next: (value: T) => unknown) {
  return (source$: Observable<T>) => source$.pipe(tap({ next: action(next) }))
}
